import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { keepPreviousData } from "@tanstack/react-query";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { z } from "zod";

import { toastError } from "@alpacahq/ui/old";

import { useGetIsLegalEntity, useGetTradingAccount } from "~/v2/api/hooks";
import useGetOwners from "~/v2/api/hooks/useGetOwners";
import type { Owner } from "~/v2/api/rest/account";
import { OnboardingLayout } from "~/v2/components/onboarding";
import { Path } from "~/v2/path";
import {
  DEFAULT_DOCUMENTS_VALUES,
  type ENTITY_TYPES,
  type EntityOnboardingHistoryState,
  REQUIRED_ERR_MSG,
  USA,
} from "../../constants";
import { usePageTimer } from "../../usePageTimer";
import { logAmplitudeEvent } from "../../util";
import { DOCUMENTS_FAQ } from "./faq";
import { DocumentsForm } from "./form";
import { type Documents, zDocuments } from "./schema";

const TAX_EXEMPT_TYPES: Partial<(typeof ENTITY_TYPES)[number][]> = [
  "c_corporation",
  "unincorporated", // AKA non-profit
];
const FOREIGN_FINANCIAL_INSTITUTION_TYPE: Partial<
  (typeof ENTITY_TYPES)[number]
> = "foreign_financial_institution";

const getTaxRequirements = (legalEntity: Owner) => {
  const tax: TaxRequirementsState = {
    isW9Required: false,
    isW8BeneRequired: false,
  };

  // W_8BENE is required if legal entity tax residence is not USA
  if (
    legalEntity.country_of_tax_residence !== USA &&
    legalEntity.country_of_tax_residence !== "USA"
  ) {
    tax.isW8BeneRequired = true;
  }

  // W9 is required for entity's from US whose entity type is non profit or c corporation
  // and are "exempt from tax under section 501(a)"
  if (
    (legalEntity.country_of_tax_residence === USA ||
      legalEntity.country_of_tax_residence === "USA") &&
    (TAX_EXEMPT_TYPES as string[]).includes(legalEntity.entity_type ?? "") &&
    legalEntity.is_exempt_from_tax_under_501a
  ) {
    tax.isW9Required = true;
  }

  // W9 is required for foreign financial institutions that are participating in FATCA
  if (
    legalEntity.entity_type === FOREIGN_FINANCIAL_INSTITUTION_TYPE &&
    legalEntity.is_participating_fatca
  ) {
    tax.isW9Required = true;
  }

  return tax;
};

export type TaxRequirementsState = {
  isW9Required: boolean;
  isW8BeneRequired: boolean;
};

export const DocumentsPage = () => {
  const history = useHistory<EntityOnboardingHistoryState>();

  const [taxRequirements, setTaxRequirements] = useState<TaxRequirementsState>({
    isW9Required: false,
    isW8BeneRequired: false,
  });

  const form = useForm<Documents>({
    resolver: zodResolver(
      zDocuments.superRefine(
        ({ w_8bene_uploaded, w9_uploaded }, refinementContext) => {
          if (!accountOpener || !legalEntity) {
            toastError("Account opener or legal entity not found");
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: REQUIRED_ERR_MSG,
            });
            return;
          }

          const { isW9Required, isW8BeneRequired } =
            getTaxRequirements(legalEntity);

          if (isW8BeneRequired && !w_8bene_uploaded) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: REQUIRED_ERR_MSG,
              path: ["w_8bene_uploaded"],
            });
          }
          if (isW9Required && !w9_uploaded) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: REQUIRED_ERR_MSG,
              path: ["w9_uploaded"],
            });
          }
        },
      ),
    ),
    mode: "onSubmit",
    defaultValues: DEFAULT_DOCUMENTS_VALUES,
  });

  const { account } = useGetTradingAccount();

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const { owners } = useGetOwners({
    accountId: account?.id || "",
    options: {
      placeholderData: keepPreviousData,
      enabled: !!account?.id,
    },
  });

  const { legalEntity, accountOpener, ubos } = useMemo(
    () => ({
      legalEntity: owners?.find((details) => details.is_legal_entity),
      accountOpener: owners?.find((details) => details.is_legal_account_opener),
      ubos: owners?.filter((details) => details.is_ubo) ?? [],
    }),
    [owners],
  );

  const pageTimer = usePageTimer("entity_onboarding_docs_page");

  const onBackClicked = async () => {
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_ENTITY_ONBOARDING_AUTH_INDIVIDUALS_UBOS, {
      isInEntityOnboarding: true,
    });
  };

  const onSubmitInvalid: SubmitErrorHandler<Documents> = (errors) => {
    toastError("Form has errors. Please check the form and try again.");
    const errorMessage = Object.values(errors)
      .map((s) => s.message)
      .join(", ");
    logAmplitudeEvent.invalidInput(errorMessage);
  };

  const onSubmit = () => {
    logAmplitudeEvent.nextClicked();
    const pageDuration = pageTimer.stop();
    logAmplitudeEvent.pageCompleted(pageDuration);
    history.push(Path.ROUTE_ENTITY_ONBOARDING_AGREEMENTS, {
      isInEntityOnboarding: true,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: the specific field deps are deliberate: accountOpener and legalEntity are refetched objects whose identity changes on every query settle, and this effect writes state, so depending on the objects would loop. The listed fields are exactly the ones getTaxRequirements reads.
  useEffect(() => {
    if (!accountOpener || !legalEntity) {
      return;
    }
    const tax = getTaxRequirements(legalEntity);

    if (Object.values(tax).some(Boolean)) {
      setTaxRequirements((prevState) => ({
        ...prevState,
        ...tax,
      }));
    }
  }, [
    accountOpener?.country_of_tax_residence,
    legalEntity?.entity_type,
    legalEntity?.country_of_incorporation,
    legalEntity?.is_exempt_from_tax_under_501a,
    legalEntity?.is_participating_fatca,
  ]);

  if (
    account &&
    !isLegalEntity &&
    !isLegalEntityLoading &&
    !history?.location?.state?.isInEntityOnboarding
  ) {
    history.replace("/brokerage/new-account");
  }

  if (owners && owners.length >= 1 && !ubos.length) {
    history.replace(Path.ROUTE_ENTITY_ONBOARDING_AUTH_INDIVIDUALS_UBOS, {
      isInEntityOnboarding: true,
    });
  }

  const isLoading =
    !account?.id ||
    !legalEntity?.owner_id ||
    !accountOpener?.owner_id ||
    !ubos.length;

  return (
    <OnboardingLayout
      title="Upload Business Documents"
      percentage={75}
      isLoading={isLoading}
      faq={DOCUMENTS_FAQ}
    >
      <DocumentsForm
        accountId={account?.id ?? ""}
        legalEntityOwnerId={legalEntity?.owner_id ?? ""}
        form={form}
        taxRequirements={taxRequirements}
        onBackClicked={onBackClicked}
        onSubmitClicked={form.handleSubmit(onSubmit, onSubmitInvalid)}
      />
    </OnboardingLayout>
  );
};

export default DocumentsPage;
